import { FC, useMemo } from "react";

import { Navigate, NoContextOutlet, RouteObject } from "src/router";
import { DetailPage } from "src/components/layout";
import { useUser } from "src/contexts/user-context";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { DetailPageTab } from "src/components/layout/detail-page";
import { useHasSupportAccessControl } from "src/hooks/use-has-support-access-control";
import { lazyNamed } from "src/utils/lazy-named";
import type { RoutePreload } from "src/router/route-preloads";
const ManageSupportAccess = lazyNamed(
  () => import("src/components/support-access"),
  "ManageSupportAccess",
);
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { EntitledRoute } from "src/components/router/entitled-route";

const APIKeys = lazyNamed(() => import("./api-keys"), "APIKeys");
const AuditLog = lazyNamed(() => import("./audit-log"), "AuditLog");
const EntitlementCenter = lazyNamed(
  () => import("./entitlement-center"),
  "EntitlementCenter",
);
const CloudProviders = lazyNamed(
  () => import("./cloud-providers"),
  "CloudProviders",
);
const Members = lazyNamed(() => import("./members"), "Members");
const Storage = lazyNamed(() => import("./storage"), "Storage");
const Tunnels = lazyNamed(() => import("./tunnels"), "Tunnels");
const workspaceImport = () => import("./workspace");
const Workspace = lazyNamed(workspaceImport, "Workspace");

export const preloads: RoutePreload[] = [["/settings", workspaceImport]];
const Groups = lazyNamed(() => import("./groups"), "Groups");
const Spaces = lazyNamed(() => import("./spaces"), "Spaces");
const RoleWorkspaceGrants = lazyNamed(
  () => import("src/pages/organizations/roles/role/workspaces/workspace"),
  "RoleWorkspacePolicy",
);
const DestinationGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/destinations"
    ),
  "DestinationGrants",
);
const SourceGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/sources"),
  "SourceGrants",
);
const ParentModelGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/parent-models"
    ),
  "ParentModelGrants",
);
const GeneralGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/general"),
  "GeneralGrants",
);
const AgentsGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/agents"),
  "AgentsGrants",
);
const CepConsentGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/cep-consent"
    ),
  "CepConsentGrants",
);
const NativeDeliveryGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/native-delivery"
    ),
  "NativeDeliveryGrants",
);
const DecisionEngineGrants = lazyNamed(
  () =>
    import(
      "src/pages/organizations/roles/role/workspaces/workspace/decision-engines"
    ),
  "DecisionEngineGrants",
);

const SpaceGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/spaces"),
  "SpaceGrants",
);
const EventsGrants = lazyNamed(
  () =>
    import("src/pages/organizations/roles/role/workspaces/workspace/events"),
  "EventsGrants",
);

// SSO is managed at the organization level.
const SSORoute: FC = () => {
  return <Navigate to="/organization/sso" replace />;
};

// Billing is managed at the organization level.
const BillingRoute: FC = () => {
  return <Navigate to="/organization/billing" replace />;
};

const Layout: FC = () => {
  const { user, workspace } = useUser();
  const { isPermitted: hasUpdatePermission } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_update" },
  });
  const { isPermitted: isWorkspaceAdmin } = useResourcePermission({
    v2: { resource: "workspace", grant: "is_workspace_admin" },
  });
  const { permission: userSupportAccessSettingsPermission } =
    useHasSupportAccessControl();

  const tabs: DetailPageTab[] = useMemo(() => {
    // Staff-only tool: shown to any HT staff (can_impersonate = admin_type IS
    // NOT NULL) and always in local dev, independent of the workspace-admin gate
    // below — an internal user who isn't a workspace admin should still see it.
    const entitlementCenterTabs: DetailPageTab[] =
      user?.can_impersonate || SHOW_ENTITLEMENT_CENTER_LOCALLY
        ? [ENTITLEMENT_CENTER_TAB]
        : [];

    const BASE_TABS: DetailPageTab[] = [
      CLOUD_PROVIDERS_TAB,
      API_KEYS_TAB,
      STORAGE_TAB,
      TUNNELS_TAB,
      AUDIT_LOG_TAB,
    ];
    if (hasUpdatePermission || user?.is_impersonating) {
      const canSeeSpaces = workspace?.spaces_enabled && isWorkspaceAdmin;
      return [
        WORKSPACE_TAB,
        MEMBERS_TAB,
        GROUPS_TAB,
        ...(canSeeSpaces ? [SPACES_TAB] : []),
        ...BASE_TABS,
        ...entitlementCenterTabs,
        {
          ...SUPPORT_ACCESS_TAB,
          isHidden: !userSupportAccessSettingsPermission,
        },
      ];
    }
    return [
      MEMBERS_TAB,
      ...entitlementCenterTabs,
      {
        ...SUPPORT_ACCESS_TAB,
        isHidden: !userSupportAccessSettingsPermission,
      },
    ];
  }, [
    userSupportAccessSettingsPermission,
    hasUpdatePermission,
    user?.is_impersonating,
    user?.can_impersonate,
    workspace?.spaces_enabled,
    isWorkspaceAdmin,
  ]);

  return (
    <DetailPage
      tabs={tabs}
      tabDepth={3}
      heading="Workspace settings"
      hasBottomPadding
    >
      <NoContextOutlet />
    </DetailPage>
  );
};

const MEMBERS_TAB: DetailPageTab = {
  title: "Members",
  path: "members",
  variant: "table",
};

const AUDIT_LOG_TAB: DetailPageTab = {
  title: "Audit log",
  path: "audit-log",
  variant: "table",
};

const SUPPORT_ACCESS_TAB: DetailPageTab = {
  title: "Support access",
  path: "support-access",
  variant: "form",
};

const WORKSPACE_TAB: DetailPageTab = {
  title: "Workspace",
  path: "workspace",
  variant: "form",
};

const GROUPS_TAB: DetailPageTab = {
  title: "Groups",
  path: "groups",
  variant: "table",
};

const SPACES_TAB: DetailPageTab = {
  title: "Spaces",
  path: "spaces",
  variant: "table",
};

const CLOUD_PROVIDERS_TAB: DetailPageTab = {
  title: "Cloud providers",
  path: "cloud-providers",
  variant: "table",
};
const API_KEYS_TAB: DetailPageTab = {
  title: "API keys",
  path: "api-keys",
  variant: "table",
};

const STORAGE_TAB: DetailPageTab = {
  title: "Storage",
  path: "storage",
  variant: "form",
};

const TUNNELS_TAB: DetailPageTab = {
  title: "Tunnels",
  path: "tunnels",
  variant: "table",
};

const ENTITLEMENT_CENTER_TAB: DetailPageTab = {
  title: "Entitlement Center",
  path: "entitlement-center",
  variant: "table",
};

// Staff-only tool. In local dev (vite `import.meta.env.DEV`) it's shown to any
// admin so the page can be exercised without seeding an `admin_type`; the
// backend independently enforces staff access in deployed environments.
const SHOW_ENTITLEMENT_CENTER_LOCALLY = import.meta.env.DEV;

export const routes: Array<RouteObject> = [
  {
    path: "settings/*",
    element: <Layout />,
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        path: "workspace",
        element: <Workspace />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "members",
        element: <Members />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "groups",
        element: <Groups />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            path: ":roleId/workspaces/:workspaceId",
            element: <RoleWorkspaceGrants redirectToWorkspaceSettings />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    to={{ pathname: "general", search: location.search }}
                    replace
                  />
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "general",
                element: <GeneralGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "spaces",
                element: (
                  <PermissionedRoute
                    permission={{
                      v2: {
                        resource: "workspace",
                        grant: "is_workspace_admin",
                      },
                    }}
                    redirect="general"
                  >
                    <SpaceGrants />
                  </PermissionedRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "sources",
                element: <SourceGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "destinations",
                element: <DestinationGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "parent-models",
                element: <ParentModelGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "agents",
                element: <AgentsGrants />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "ai-decisioning",
                element: (
                  <EntitledRoute
                    entitlement="decisionEngine"
                    redirect="general"
                  >
                    <DecisionEngineGrants />
                  </EntitledRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "cep-consent",
                element: (
                  <EntitledRoute entitlement="cep" redirect="general">
                    <CepConsentGrants />
                  </EntitledRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "native-delivery",
                element: (
                  <EntitledRoute entitlement="cep" redirect="general">
                    <NativeDeliveryGrants />
                  </EntitledRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: "events/*",
                element: (
                  <FeatureFlagRoute
                    flagKey="eventsRbacEnabled"
                    redirect="general"
                  >
                    <EventsGrants />
                  </FeatureFlagRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
        ],
      },
      {
        path: "spaces/*",
        element: (
          <PermissionedRoute
            permission={{
              v2: { resource: "workspace", grant: "is_workspace_admin" },
            }}
            redirect="/settings/members"
          >
            <Spaces />
          </PermissionedRoute>
        ),
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "billing",
        element: <BillingRoute />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "cloud-providers",
        element: <CloudProviders />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "api-keys",
        element: <APIKeys />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "storage",
        element: <Storage />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "tunnels/*",
        element: <Tunnels />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "sso",
        element: <SSORoute />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "audit-log",
        element: <AuditLog />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "entitlement-center",
        element: <EntitlementCenter />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        path: "support-access",
        element: <ManageSupportAccess level="workspace" />,
        spacesAccess: "hidden-from-limited-view",
      },
      {
        index: true,
        element: (
          <Navigate
            to={{ pathname: "members", search: location.search }}
            replace
          />
        ),
        spacesAccess: "hidden-from-limited-view",
      },
    ],
  },
];
